import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { sessionQueryOptions, unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { cartPageQueryOptions } from "~/app/api/pageData/cartPageQuery";
import type { CartPageInitialData } from "~/app/api/pageData/cartPageInitialData";
import { quoteCartPageQueryOptions } from "~/app/api/pageData/quoteCartPageQuery";
import type { QuoteCartPageInitialData } from "~/app/api/pageData/quoteCartPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createCartPage } from "~/pages/CartPage/CartPage";
import { create as createActiveQuotePage } from "~/pages/ActiveQuotePage/ActiveQuotePage";
import { clearSalesPartnerForActiveB2CCart } from "~/features/cart/cart/api/clearSalesPartner";
import { activeCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";
import { userRolesAreAuthenticated, userRolesAreRegistered, userRolesAreSalesman } from "~/domains/user/utils/roles";
import { occCartUserPathFor } from "~/app/api/occ/occUserPath";

export type CartRouteData =
  | { page: "cart"; pageData: PageInitialData<CartPageInitialData> }
  | { page: "activeQuote"; pageData: PageInitialData<QuoteCartPageInitialData> };

function CartRoute() {
  const result = useLoaderData({
    from: "/cart",
  }) as CartRouteData;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.pageData.metadata}>
      {result.page === "activeQuote"
        ? createActiveQuotePage()
        : createCartPage({
            disableCheckout: result.pageData.data?.disableCheckout ?? false,
            showSubtotal: result.pageData.data?.showSubtotal ?? false,
          })}
    </StorefrontShellFrame>
  );
}

export async function loadCartPageData(context: { queryClient: QueryClient }) {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
  const roles = session.user.roles;
  if (userRolesAreSalesman(roles)) {
    const result = await context.queryClient.ensureQueryData(quoteCartPageQueryOptions());
    return { page: "activeQuote", pageData: unwrapPageInitialData(result) } satisfies CartRouteData;
  }

  const authenticated = userRolesAreAuthenticated(roles);
  const activeCartId = activeCartResourceId(session.activeCart, authenticated);
  await clearSalesPartnerForActiveB2CCart({
    activeCartId,
    customer: authenticated && !userRolesAreRegistered(roles),
    userPath: occCartUserPathFor(authenticated),
  });
  const result = await context.queryClient.ensureQueryData(cartPageQueryOptions(activeCartId));

  return { page: "cart", pageData: unwrapPageInitialData(result) } satisfies CartRouteData;
}

export const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cart",
  loader: async ({ context }) => loadCartPageData(context),
  component: CartRoute,
});
